<template>
  <footer id="Footer">
    <!-- Top CTA strip — pulls the eye into "start a trial" before the link grid -->
    <div class="footer-cta">
      <div class="container footer-cta-inner">
        <div class="footer-cta-text">
          <h3>Ready to run your building the easy way?</h3>
          <p>
            Start the 60-day free trial. No card, no commitment — and we help
            you move your existing tenants in for free.
          </p>
        </div>
        <div class="footer-cta-actions">
          <a href="#contact" class="footer-cta-btn primary">
            Start free trial <i class="fa-solid fa-arrow-right"></i>
          </a>
          <a href="https://wa.me/250789178645" target="_blank" rel="noopener" class="footer-cta-btn ghost">
            <i class="fa-brands fa-whatsapp"></i>
            Chat on WhatsApp
          </a>
        </div>
      </div>
    </div>

    <!-- Main link grid -->
    <div class="footer-main">
      <div class="container footer-grid">
        <!-- Brand column -->
        <div class="footer-col brand">
          <div class="footer-brand">
            <span class="footer-brand-mark">i</span>
            <span class="footer-brand-name">Ireme <strong>PMS</strong></span>
          </div>
          <p class="footer-brand-blurb">
            One simple home for everything that runs your building — tenants,
            bills, contracts and reports. Built in Rwanda, for Rwanda.
          </p>
          <div class="footer-socials">
            <a href="https://wa.me/250789178645" target="_blank" rel="noopener" aria-label="WhatsApp">
              <i class="fa-brands fa-whatsapp"></i>
            </a>
            <a href="https://x.com" target="_blank" rel="noopener" aria-label="X">
              <i class="fa-brands fa-x-twitter"></i>
            </a>
            <a href="https://www.linkedin.com" target="_blank" rel="noopener" aria-label="LinkedIn">
              <i class="fa-brands fa-linkedin-in"></i>
            </a>
            <a href="https://www.instagram.com" target="_blank" rel="noopener" aria-label="Instagram">
              <i class="fa-brands fa-instagram"></i>
            </a>
            <a href="https://www.facebook.com" target="_blank" rel="noopener" aria-label="Facebook">
              <i class="fa-brands fa-facebook-f"></i>
            </a>
          </div>
        </div>

        <!-- Product links -->
        <div class="footer-col">
          <h4>Product</h4>
          <ul>
            <li><a href="#features">What's inside</a></li>
            <li><a href="#how-it-works">How it works</a></li>
            <li><a href="#pricing">Pricing</a></li>
            <li><a href="#faqs">Questions, answered</a></li>
          </ul>
        </div>

        <!-- Company links -->
        <div class="footer-col">
          <h4>Company</h4>
          <ul>
            <li><a href="https://iremetech.com" target="_blank" rel="noopener">About Ireme</a></li>
            <li><a href="#contact">Contact us</a></li>
            <li><a href="#contact">Book a demo</a></li>
            <li><a href="#contact">Support</a></li>
          </ul>
        </div>

        <!-- Contact column -->
        <div class="footer-col contact">
          <h4>Get in touch</h4>
          <ul class="footer-contact-list">
            <li>
              <i class="fa-solid fa-location-dot"></i>
              <span>Kigali, Rwanda</span>
            </li>
            <li>
              <i class="fa-solid fa-phone"></i>
              <a href="tel:+250789178645">+250 789 178 645</a>
            </li>
            <li>
              <i class="fa-solid fa-envelope"></i>
              <a href="mailto:info@iremetech.com">info@iremetech.com</a>
            </li>
            <li class="footer-hours">
              <i class="fa-regular fa-clock"></i>
              <span>Mon – Fri · 8am – 6pm CAT</span>
            </li>
          </ul>
        </div>
      </div>
    </div>

    <!-- Legal bottom strip -->
    <div class="footer-bottom">
      <div class="container footer-bottom-inner">
        <p class="copyright">
          © {{ year }} Ireme PMS. A product of
          <a href="https://iremetech.com" target="_blank" rel="noopener">Ireme Technologies</a>.
          All rights reserved.
        </p>
        <ul class="footer-legal">
          <li><a href="#">Privacy</a></li>
          <li><a href="#">Terms</a></li>
          <li><a href="#">Status</a></li>
        </ul>
      </div>
    </div>
  </footer>
</template>

<script>
export default {
  computed: {
    year() {
      return new Date().getFullYear();
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/main.scss";

#Footer {
  width: 100%;
  background: #0f172a;
  color: #cbd5e1;
  font-feature-settings: "ss01", "cv11";

  .container { max-width: 1200px; margin: 0 auto; padding: 0 24px; }

  /* ── Top CTA strip ──────────────────────────────────────────────── */
  .footer-cta {
    background: linear-gradient(135deg, $primary_color 0%, #7c3aed 100%);
    padding: 36px 0;
  }
  .footer-cta-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    color: #fff;
  }
  .footer-cta-text {
    flex: 1;
    h3 {
      margin: 0 0 6px;
      font-size: 1.4rem;
      font-weight: 700;
      letter-spacing: -0.01em;
    }
    p {
      margin: 0;
      font-size: 0.92rem;
      opacity: 0.92;
      line-height: 1.5;
      max-width: 580px;
    }
  }
  .footer-cta-actions {
    display: flex;
    gap: 10px;
    flex-shrink: 0;
    flex-wrap: wrap;
  }
  .footer-cta-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 11px 18px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;
    transition: transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
    &:hover { transform: translateY(-1px); }
    &.primary {
      background: #fff;
      color: $primary_color;
      &:hover { box-shadow: 0 12px 24px rgba(0, 0, 0, 0.25); }
    }
    &.ghost {
      background: transparent;
      color: #fff;
      border: 1px solid rgba(255, 255, 255, 0.4);
      &:hover { background: rgba(255, 255, 255, 0.08); }
    }
  }

  /* ── Main link grid ─────────────────────────────────────────────── */
  .footer-main {
    padding: 64px 0 40px;
    background: #0f172a;
  }
  .footer-grid {
    display: grid;
    grid-template-columns: 1.6fr 1fr 1fr 1.3fr;
    gap: 48px;
  }
  .footer-col {
    h4 {
      color: #ffffff;
      font-size: 0.82rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
      margin: 0 0 16px;
    }
    ul {
      list-style: none;
      padding: 0;
      margin: 0;
      display: flex;
      flex-direction: column;
      gap: 10px;
      li a {
        color: #94a3b8;
        text-decoration: none;
        font-size: 0.92rem;
        transition: color 0.15s ease;
        &:hover { color: #fff; }
      }
    }
  }

  /* Brand column */
  .footer-brand {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 14px;
  }
  .footer-brand-mark {
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: linear-gradient(135deg, $primary_color 0%, #7c3aed 100%);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #fff;
    font-weight: 800;
    font-size: 1.1rem;
    font-family: ui-sans-serif, system-ui, -apple-system;
  }
  .footer-brand-name {
    font-size: 1.15rem;
    font-weight: 500;
    color: #fff;
    letter-spacing: -0.01em;
    strong { font-weight: 800; }
  }
  .footer-brand-blurb {
    color: #94a3b8;
    font-size: 0.92rem;
    line-height: 1.6;
    margin: 0 0 22px;
    max-width: 320px;
  }
  .footer-socials {
    display: flex;
    gap: 8px;
    a {
      width: 36px;
      height: 36px;
      border-radius: 10px;
      background: rgba(255, 255, 255, 0.06);
      border: 1px solid rgba(255, 255, 255, 0.08);
      color: #cbd5e1;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      transition: background 0.15s ease, color 0.15s ease, border-color 0.15s ease, transform 0.15s ease;
      font-size: 0.95rem;
      text-decoration: none;
      &:hover {
        background: $primary_color;
        color: #fff;
        border-color: transparent;
        transform: translateY(-1px);
      }
    }
  }

  /* Contact column */
  .footer-col.contact {
    .footer-contact-list {
      gap: 12px;
      li {
        display: flex;
        align-items: center;
        gap: 12px;
        color: #cbd5e1;
        font-size: 0.92rem;
        i {
          width: 18px;
          color: $primary_color;
          font-size: 0.9rem;
          flex-shrink: 0;
          text-align: center;
        }
        a {
          color: #cbd5e1;
          text-decoration: none;
          transition: color 0.15s ease;
          &:hover { color: #fff; }
        }
      }
    }
    .footer-hours {
      color: #94a3b8 !important;
      font-size: 0.85rem !important;
    }
  }

  /* ── Legal bottom strip ─────────────────────────────────────────── */
  .footer-bottom {
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    background: #0b1325;
    padding: 18px 0;
  }
  .footer-bottom-inner {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
  }
  .copyright {
    margin: 0;
    color: #64748b;
    font-size: 0.82rem;
    a {
      color: #cbd5e1;
      text-decoration: none;
      font-weight: 500;
      &:hover { color: #fff; text-decoration: underline; }
    }
  }
  .footer-legal {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    gap: 22px;
    li a {
      color: #64748b;
      font-size: 0.82rem;
      text-decoration: none;
      transition: color 0.15s ease;
      &:hover { color: #fff; }
    }
  }

  /* ── Responsive ─────────────────────────────────────────────────── */
  @media (max-width: 960px) {
    .footer-grid {
      grid-template-columns: 1fr 1fr;
      gap: 36px;
    }
    .footer-cta-inner {
      flex-direction: column;
      align-items: flex-start;
      gap: 18px;
    }
  }
  @media (max-width: 560px) {
    .footer-grid { grid-template-columns: 1fr; gap: 32px; }
    .footer-cta { padding: 28px 0; }
    .footer-cta-text h3 { font-size: 1.15rem; }
    .footer-main { padding: 48px 0 32px; }
    .footer-bottom-inner { flex-direction: column; align-items: flex-start; }
    .footer-legal { gap: 14px; flex-wrap: wrap; }
    .footer-cta-actions { width: 100%; }
    .footer-cta-btn { flex: 1; justify-content: center; }
  }
}
</style>
